import React, { useState, useEffect, useCallback } from 'react';
import { useRestaurant } from '../../contexts/RestaurantContext';
import { 
  Plus, 
  Calendar, 
  Clock, 
  Ban, 
  Edit, 
  Trash2, 
  ToggleLeft, 
  Filter, 
  RefreshCw,
  AlertCircle,
  CheckCircle,
  X
} from 'lucide-react';
import { AvailabilityClosure, CreateClosureData, ClosureFilters } from '../../types';
import apiService from '../../services/api';

const AvailabilityClosures: React.FC = () => {
  const { restaurant } = useRestaurant();
  const [closures, setClosures] = useState<AvailabilityClosure[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingClosure, setEditingClosure] = useState<AvailabilityClosure | null>(null);
  const [filters, setFilters] = useState<ClosureFilters>({});
  const [showFilters, setShowFilters] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Form state
  const [formData, setFormData] = useState<CreateClosureData>({
    restaurante_id: restaurant?.id || 0,
    tipo: 'dia_completo',
    data_inicio: '',
    motivo: ''
  });

  const fetchClosures = useCallback(async () => {
    if (!restaurant?.id) return;

    try {
      setIsLoading(true);
      const response = await apiService.getClosures({
        ...filters,
      });
      setClosures(response.data);
    } catch (error) {
      console.error('Failed to fetch closures:', error);
      setError('Erro ao carregar fechamentos');
    } finally {
      setIsLoading(false);
    }
  }, [restaurant?.id, filters]);

  useEffect(() => {
    if (restaurant?.id) {
      fetchClosures();
    }
  }, [fetchClosures, restaurant?.id]);

  useEffect(() => {
    if (restaurant?.id) {
      setFormData(prev => ({ ...prev, restaurante_id: restaurant.id }));
    }
  }, [restaurant?.id]);

  const clearMessages = () => {
    setError(null);
    setSuccess(null);
  };

  const handleCreateClosure = async (e: React.FormEvent) => {
    e.preventDefault();
    clearMessages();

    try {
      await apiService.createClosure(formData);
      setSuccess('Fechamento criado com sucesso!');
      setShowCreateForm(false);
      resetForm();
      fetchClosures();
    } catch (error: any) {
      setError(error.response?.data?.message || 'Erro ao criar fechamento');
    }
  };

  const handleUpdateClosure = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingClosure) return;
    clearMessages();

    try {
      const updateData = {
        tipo: formData.tipo,
        data_inicio: formData.data_inicio,
        data_fim: formData.data_fim,
        hora_inicio: formData.hora_inicio,
        hora_fim: formData.hora_fim,
        motivo: formData.motivo
      };
      await apiService.updateClosure(editingClosure.id, updateData);
      setSuccess('Fechamento atualizado com sucesso!');
      setEditingClosure(null);
      resetForm();
      fetchClosures();
    } catch (error: any) {
      setError(error.response?.data?.message || 'Erro ao atualizar fechamento');
    }
  };

  const handleToggleClosure = async (id: number, ativo: boolean) => {
    clearMessages();
    try {
      await apiService.toggleClosure(id, !ativo);
      setSuccess(`Fechamento ${!ativo ? 'ativado' : 'desativado'} com sucesso!`);
      fetchClosures();
    } catch (error: any) {
      setError(error.response?.data?.message || 'Erro ao alterar estado do fechamento');
    }
  };

  const handleDeleteClosure = async (id: number) => {
    if (!window.confirm('Tem certeza de que deseja eliminar este fechamento?')) return;
    clearMessages();

    try {
      await apiService.deleteClosure(id);
      setSuccess('Fechamento eliminado com sucesso!');
      fetchClosures();
    } catch (error: any) {
      setError(error.response?.data?.message || 'Erro ao eliminar fechamento');
    }
  };

  const resetForm = () => {
    setFormData({
      restaurante_id: restaurant?.id || 0,
      tipo: 'dia_completo',
      data_inicio: '',
      motivo: ''
    });
  };

  const startEdit = (closure: AvailabilityClosure) => {
    setEditingClosure(closure);
    setFormData({
      restaurante_id: closure.restaurante_id,
      tipo: closure.tipo,
      data_inicio: closure.data_inicio,
      data_fim: closure.data_fim,
      hora_inicio: closure.hora_inicio,
      hora_fim: closure.hora_fim,
      motivo: closure.motivo
    });
    setShowCreateForm(true);
  };

  const cancelEdit = () => {
    setEditingClosure(null);
    setShowCreateForm(false);
    resetForm();
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-PT', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (timeString?: string) => {
    if (!timeString) return '';
    return timeString.substring(0, 5);
  };

  if (isLoading && closures.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner mr-2" />
        <span>A carregar fechamentos...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="md:flex md:items-center md:justify-between">
        <div className="min-w-0 flex-1">
          <h1 className="text-2xl font-bold leading-7 text-secondary-900 sm:truncate sm:text-3xl">
            Gestão de Disponibilidade
          </h1>
          <p className="mt-1 text-sm text-secondary-500">
            Gerir fechamentos temporários e horários especiais do restaurante
          </p>
        </div>
        <div className="mt-4 flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-2 md:mt-0">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="btn-secondary flex items-center space-x-2"
          >
            <Filter className="h-4 w-4" />
            <span>Filtros</span>
          </button>
          <button
            onClick={fetchClosures}
            className="btn-secondary flex items-center space-x-2"
          >
            <RefreshCw className="h-4 w-4" />
            <span>Atualizar</span>
          </button>
          <button
            onClick={() => setShowCreateForm(true)}
            className="btn-primary flex items-center space-x-2"
          >
            <Plus className="h-4 w-4" />
            <span>Novo Fechamento</span>
          </button>
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <AlertCircle className="h-5 w-5 text-red-600" />
            <span className="text-sm text-red-700">{error}</span>
          </div>
          <button onClick={clearMessages} className="text-red-600 hover:text-red-800">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 rounded-md p-4 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <CheckCircle className="h-5 w-5 text-green-600" />
            <span className="text-sm text-green-700">{success}</span>
          </div>
          <button onClick={clearMessages} className="text-green-600 hover:text-green-800">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Filters */}
      {showFilters && (
        <div className="card p-4">
          <h3 className="text-lg font-medium text-secondary-900 mb-4">Filtros</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-2">
                Tipo
              </label>
              <select
                value={filters.tipo || ''}
                onChange={(e) => setFilters({ ...filters, tipo: e.target.value as any })}
                className="form-select w-full"
              >
                <option value="">Todos</option>
                <option value="dia_completo">Dia Completo</option>
                <option value="horario_especifico">Horário Específico</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-2">
                Estado
              </label>
              <select
                value={filters.ativo?.toString() || ''}
                onChange={(e) => setFilters({ 
                  ...filters, 
                  ativo: e.target.value === '' ? undefined : e.target.value === 'true' 
                })}
                className="form-select w-full"
              >
                <option value="">Todos</option>
                <option value="true">Ativo</option>
                <option value="false">Inativo</option>
              </select>
            </div>
            <div className="flex items-end">
              <button
                onClick={() => setFilters({})}
                className="btn-secondary w-full"
              >
                Limpar Filtros
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create/Edit Form */}
      {showCreateForm && (
        <div className="card p-6">
          <h3 className="text-lg font-medium text-secondary-900 mb-4">
            {editingClosure ? 'Editar Fechamento' : 'Novo Fechamento'}
          </h3>
          
          <form onSubmit={editingClosure ? handleUpdateClosure : handleCreateClosure} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">
                  Tipo de Fechamento *
                </label>
                <select
                  value={formData.tipo}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    tipo: e.target.value as 'dia_completo' | 'horario_especifico',
                    // Reset time fields when switching to full day
                    ...(e.target.value === 'dia_completo' && {
                      hora_inicio: undefined,
                      hora_fim: undefined
                    })
                  })}
                  className="form-select w-full"
                  required
                >
                  <option value="dia_completo">Dia Completo</option>
                  <option value="horario_especifico">Horário Específico</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">
                  Data de Início *
                </label>
                <input
                  type="date"
                  value={formData.data_inicio}
                  onChange={(e) => setFormData({ ...formData, data_inicio: e.target.value })}
                  className="form-input w-full"
                  required
                />
              </div>

              {formData.tipo === 'horario_especifico' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-2">
                      Hora de Início *
                    </label>
                    <input
                      type="time"
                      value={formData.hora_inicio || ''}
                      onChange={(e) => setFormData({ ...formData, hora_inicio: e.target.value })}
                      className="form-input w-full"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-2">
                      Hora de Fim *
                    </label>
                    <input
                      type="time"
                      value={formData.hora_fim || ''}
                      onChange={(e) => setFormData({ ...formData, hora_fim: e.target.value })}
                      className="form-input w-full"
                      required
                    />
                  </div>
                </>
              )}

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-secondary-700 mb-2">
                  Motivo *
                </label>
                <input
                  type="text"
                  value={formData.motivo}
                  onChange={(e) => setFormData({ ...formData, motivo: e.target.value })}
                  placeholder="Ex: Manutenção da cozinha, Feriado, Evento privado..."
                  className="form-input w-full"
                  required
                />
              </div>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={cancelEdit}
                className="btn-secondary"
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="btn-primary"
              >
                {editingClosure ? 'Atualizar' : 'Criar'} Fechamento
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Closures List */}
      <div className="card">
        <div className="px-6 py-4 border-b border-secondary-200">
          <h3 className="text-lg font-medium text-secondary-900">
            Fechamentos Programados
          </h3>
        </div>

        {closures.length === 0 ? (
          <div className="p-8 text-center">
            <Ban className="h-12 w-12 text-secondary-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-secondary-900 mb-2">
              Nenhum fechamento programado
            </h3>
            <p className="text-secondary-500 mb-4">
              Crie o primeiro fechamento para gerir a disponibilidade do restaurante.
            </p>
            <button
              onClick={() => setShowCreateForm(true)}
              className="btn-primary"
            >
              Criar Primeiro Fechamento
            </button>
          </div>
        ) : (
          <div className="divide-y divide-secondary-200">
            {closures.map((closure) => (
              <div key={closure.id} className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-start space-x-4">
                    <div className={`p-2 rounded-md ${
                      closure.tipo === 'dia_completo' 
                        ? 'bg-red-100 text-red-600' 
                        : 'bg-orange-100 text-orange-600'
                    }`}>
                      {closure.tipo === 'dia_completo' ? (
                        <Calendar className="h-5 w-5" />
                      ) : (
                        <Clock className="h-5 w-5" />
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center space-x-2">
                        <h4 className="text-lg font-medium text-secondary-900">
                          {closure.tipo === 'dia_completo' ? 'Dia Completo' : 'Horário Específico'}
                        </h4>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          closure.ativo 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {closure.ativo ? 'Ativo' : 'Inativo'}
                        </span>
                      </div>
                      <p className="text-secondary-600 mt-1">{closure.motivo}</p>
                      <div className="text-sm text-secondary-500 mt-2">
                        <p>
                          <strong>Data:</strong> {formatDate(closure.data_inicio)}
                          {closure.data_fim && closure.data_fim !== closure.data_inicio && 
                            ` até ${formatDate(closure.data_fim)}`
                          }
                        </p>
                        {closure.tipo === 'horario_especifico' && (
                          <p>
                            <strong>Horário:</strong> {formatTime(closure.hora_inicio)} - {formatTime(closure.hora_fim)}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handleToggleClosure(closure.id, closure.ativo)}
                      className={`p-2 rounded-md transition-colors ${
                        closure.ativo
                          ? 'text-green-600 hover:bg-green-50'
                          : 'text-red-600 hover:bg-red-50'
                      }`}
                      title={closure.ativo ? 'Desativar' : 'Ativar'}
                    >
                      <ToggleLeft className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => startEdit(closure)}
                      className="p-2 text-secondary-600 hover:bg-secondary-50 rounded-md transition-colors"
                      title="Editar"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteClosure(closure.id)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-md transition-colors"
                      title="Eliminar"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AvailabilityClosures;
